import { Icons, Progress, Spinner } from '@agro-club/agroclub-shared'

import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { Card } from 'views/components/Card/Card'
import { Hr, HrWithHeader } from 'views/components/Hr/Hr'
import { ProductData, User } from 'modules/domain/user/types'
import { refetchFunc, useSingleEntity } from 'modules/domain/common/hooks'
import { endpoints } from 'modules/endpoints'
import { AButton } from 'views/components/Analytics'
import ProductDropDown from './ProductDropDown'
import EditableCell from './EditableCell'
import { DASH } from 'modules/constants'
import { RemoveProductDataModal } from './RemoveProductDataModal'
import useFormatDate from 'hooks/useFormatDate'
import SowedAreaCell from './SowedAreaCell'
import { usePermissions } from 'views/components/PermissionCheck/usePermissions'

type Props = {
  user: User
  userRefetch: refetchFunc
}

const template = css`
  grid-template-columns: 180px 90px 94px 90px 24px;
`
const ProductsDataGrid = styled.div`
  display: grid;
  grid-gap: 10px;
  ${template}
  align-items: center;
`
const Header = styled.div`
  margin-bottom: 10px;
  display: grid;
  grid-gap: 10px;
  ${template}
  align-items: end;
`
const SecondRowGrid = styled.div`
  margin-top: 10px;
  display: grid;
  grid-template-columns: 290px 200px;
`

export const ProductsDataSection: FC<Props> = ({ user, userRefetch }) => {
  const { t } = useTranslation('user')
  const [progress, data, refetchProductData] = useSingleEntity<ProductData[]>(
    endpoints.companyProductData(user.profile?.company?.id || ''),
  )
  const [dataForDisplay, setDataForDisplay] = useState<ProductData[]>([])
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const formatDate = useFormatDate()
  const canDelete = usePermissions(['companies.delete_companyproductdata'])
  const canEdit = usePermissions(['companies.change_companyproductdata'])
  const canAdd = usePermissions(['companies.add_companyproductdata'])

  //const columns = useProductsColumns(user, refetchProductData)

  useEffect(() => {
    if (progress != Progress.SUCCESS || !data) return
    setDataForDisplay(data)
  }, [data, progress])

  if (!user.profile?.company?.id) return null

  const companyId = parseInt(user.profile.company.id)

  const onAdd = () => {
    setDataForDisplay([...dataForDisplay, {}])
  }

  if (progress == Progress.WORK) return <Spinner />

  const onDelete = (id: number | undefined, index: number) => {
    if (!id) {
      setDataForDisplay(dataForDisplay.filter((el, i) => i !== index))
    } else {
      setDeleteId(id)
    }
  }

  let table

  if (dataForDisplay.length === 0) {
    table = <div style={{ display: 'flex', justifyContent: 'center' }}>{t('common:noData')}</div>
  } else {
    table = (
      <div>
        <Header>
          <div>{t('productDataLabels.product')}</div>
          <div>{t('productDataLabels.sowed_area')}</div>
          <div>{t('productDataLabels.potential_quantity')}</div>
          <div>{t('productDataLabels.actual_quantity_left')}</div>
        </Header>
        {dataForDisplay.map((row, index) => {
          return (
            <div key={index}>
              <Hr />
              <ProductsDataGrid>
                <ProductDropDown data={row} user={user} refetch={refetchProductData} disabled={!canEdit} />
                <EditableCell
                  data={row}
                  user={user}
                  refetch={refetchProductData}
                  fieldName="sowed_area"
                  disabled={!canEdit}
                />
                <div>{row.potential_quantity ?? DASH}</div>
                <EditableCell
                  data={row}
                  user={user}
                  refetch={refetchProductData}
                  fieldName="actual_quantity_left"
                  disabled={!canEdit}
                />
                <AButton
                  size="small"
                  id="delete"
                  color="secondary"
                  Icon={Icons.IconDelete}
                  onClick={() => onDelete(row.id, index)}
                  disabled={!canDelete}
                />
              </ProductsDataGrid>
              <SecondRowGrid>
                <div>
                  {t('productDataLabels.modified')}:{' '}
                  {row.modified ? formatDate(row.modified, { withTime: true }) : DASH}{' '}
                </div>
                <div>
                  {t('productDataLabels.sampleDate')}:{' '}
                  {row.sample_published_date ? formatDate(row.sample_published_date) : DASH}
                </div>
              </SecondRowGrid>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <Card.GapableContent>
      <SowedAreaCell user={user} refetch={userRefetch} />
      <HrWithHeader text={t('productDataLabels.productsInfo')} />
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'end', marginBottom: '8px' }}>
          <AButton
            Icon={Icons.IconAdd}
            id="changeDealStatus"
            size="small"
            color="approve"
            onClick={onAdd}
            disabled={!canAdd}
          >
            {t('common:add')}
          </AButton>
        </div>
        {table}
        {deleteId && (
          <RemoveProductDataModal
            companyId={companyId}
            id={deleteId}
            onClose={() => setDeleteId(null)}
            onSuccess={async () => {
              await refetchProductData(true)
              setDeleteId(null)
            }}
          />
        )}
      </div>
    </Card.GapableContent>
  )
}
